/** code from https://github.com/angular/material2 */

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterContentChecked,
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  Optional,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { merge } from 'rxjs/observable/merge';
import { of as observableOf } from 'rxjs/observable/of';
import { auditTime } from 'rxjs/operators/auditTime';
import { startWith } from 'rxjs/operators/startWith';

import { YztTabLabelDirective } from './yzt-tab-label.directive';
import { YztTabsInkBarDirective } from './yzt-tabs-ink-bar.directive';
import { toBoolean } from '../util/convert';

const /** @type {?} */ EXAGGERATED_OVERSCROLL = 64;
export type ScrollDirection = 'after' | 'before';

/** duplicated defined https://github.com/angular/angular-cli/issues/2034 **/
export type NzTabPositionMode = 'horizontal' | 'vertical';
export class YztTabsNavComponent implements AfterContentChecked, AfterContentInit {
private _animated = true;
private _hideBar = false;
private _showPagination = true;

  _showPaginationControls = false;
  _disableScrollAfter = true;
  _disableScrollBefore = true;
  _scrollDistance = 0;
  _selectedIndexChanged = false;
  _realignInkBar: Subscription | null = null;
  _tabLabelCount: number;
  _scrollDistanceChanged: boolean;
  _selectedIndex = 0;
  _tabPositionMode: NzTabPositionMode = 'horizontal';
   nzSize = 'default';
  _type = 'line';
/**
 * @param {?} value
 * @return {?}
 */
set nzAnimated(value: boolean) {
    this._animated = toBoolean(value);
  }
/**
 * @return {?}
 */
get nzAnimated(): boolean {
    return this._animated;
  }
/**
 * @param {?} value
 * @return {?}
 */
set nzHideBar(value: boolean) {
    this._hideBar = toBoolean(value);
  }
/**
 * @return {?}
 */
get nzHideBar(): boolean {
    return this._hideBar;
  }
/**
 * @param {?} value
 * @return {?}
 */
set nzType(value: string) {
    this._type = value;
    if (this._type !== 'line') {
      this._inkBar.setDisplay('none');
    } else {
      this._inkBar.setDisplay('block');
    }
  }
/**
 * @return {?}
 */
get nzType(): string {
    return this._type;
  }

   _tabBarExtraContent: TemplateRef<void>;
   _labelWrappers: QueryList<YztTabLabelDirective>;
   _inkBar: YztTabsInkBarDirective;
   _tabListContainer: ElementRef;
   _tabList: ElementRef;
/**
 * @param {?} value
 * @return {?}
 */
set nzShowPagination(value: boolean) {
    this._showPagination = toBoolean(value);
  }
/**
 * @return {?}
 */
get nzShowPagination(): boolean {
    return this._showPagination;
  }
/**
 * @param {?} value
 * @return {?}
 */
set nzPositionMode(value: NzTabPositionMode) {
    this._tabPositionMode = value;
    this._alignInkBarToSelectedTab();
    if (this.nzShowPagination) {
      this._updatePagination();
    }
  }
/**
 * @return {?}
 */
get nzPositionMode(): NzTabPositionMode {
    return this._tabPositionMode;
  }
/**
 * @param {?} value
 * @return {?}
 */
set selectedIndex(value: number) {
    this._selectedIndexChanged = this._selectedIndex !== value;

    this._selectedIndex = value;
  }
/**
 * @return {?}
 */
get selectedIndex(): number {
    return this._selectedIndex;
  }
/**
 * @param {?} _elementRef
 * @param {?} _ngZone
 * @param {?} _renderer
 * @param {?} _dir
 */
constructor(public _elementRef: ElementRef,
private _ngZone: NgZone,
private _renderer: Renderer2,
private _dir: Directionality) {
  }
/**
 * @return {?}
 */
_onContentChanges(): void {
    if (this.nzShowPagination) {
      this._updatePagination();
    }
    this._alignInkBarToSelectedTab();
  }
/**
 * @param {?} scrollDir
 * @return {?}
 */
_scrollHeader(scrollDir: ScrollDirection): void {

    // Move the scroll distance one-third the length of the tab list's viewport.
    this.scrollDistance += (scrollDir === 'before' ? -1 : 1) * this.viewWidthHeightPix / 3;
  }
/**
 * @return {?}
 */
ngAfterContentChecked(): void {

    if (this._tabLabelCount !== this._labelWrappers.length) {
      if (this.nzShowPagination) {
        this._updatePagination();
      }
      this._tabLabelCount = this._labelWrappers.length;
    }
    if (this._selectedIndexChanged) {
      this._scrollToLabel(this._selectedIndex);
      if (this.nzShowPagination) {
        this._checkScrollingControls();
      }
      this._alignInkBarToSelectedTab();
      this._selectedIndexChanged = false;
    }
    if (this._scrollDistanceChanged) {
      if (this.nzShowPagination) {
        this._updateTabScrollPosition();
      }
      this._scrollDistanceChanged = false;
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this._realignInkBar = this._ngZone.runOutsideAngular(() => {
      const /** @type {?} */ dirChange = this._dir ? this._dir.change : observableOf(null);
      const /** @type {?} */ resize = typeof window !== 'undefined' ?
        fromEvent(window, 'resize').pipe(auditTime(10)) :
        observableOf(null);
      return merge(dirChange, resize).pipe(startWith(null)).subscribe(() => {
        if (this.nzShowPagination) {
          this._updatePagination();
        }
        this._alignInkBarToSelectedTab();
      });
    });
  }
/**
 * @return {?}
 */
_updateTabScrollPosition(): void {
    const /** @type {?} */ scrollDistance = this.scrollDistance;
    if (this.nzPositionMode === 'horizontal') {
      const /** @type {?} */ translateX = this._getLayoutDirection() === 'ltr' ? -scrollDistance : scrollDistance;
      this._renderer.setStyle(this._tabList.nativeElement, 'transform',
        `translate3d(${translateX}px, 0, 0)`);
    } else {
      this._renderer.setStyle(this._tabList.nativeElement, 'transform',
        `translate3d(0,${-scrollDistance}px, 0)`);
    }
  }
/**
 * @return {?}
 */
_updatePagination(): void {
    this._checkPaginationEnabled();
    this._checkScrollingControls();
    this._updateTabScrollPosition();
  }
/**
 * @return {?}
 */
_checkPaginationEnabled(): void {
    this._showPaginationControls =
      this.tabListScrollWidthHeightPix > this.elementRefOffSetWidthHeight;

    if (!this._showPaginationControls) {
      this.scrollDistance = 0;
    }
  }
/**
 * @param {?} labelIndex
 * @return {?}
 */
_scrollToLabel(labelIndex: number): void {
    const /** @type {?} */ selectedLabel = this._labelWrappers
      ? this._labelWrappers.toArray()[ labelIndex ]
      : null;

    if (!selectedLabel) {
      return;
    }

    // The view length is the visible width of the tab labels.

    let /** @type {?} */ labelBeforePos: number;
    let /** @type {?} */ labelAfterPos: number;
    if (this.nzPositionMode === 'horizontal') {
      if (this._getLayoutDirection() === 'ltr') {
        labelBeforePos = selectedLabel.getOffsetLeft();
        labelAfterPos = labelBeforePos + selectedLabel.getOffsetWidth();
      } else {
        labelAfterPos = this._tabList.nativeElement.offsetWidth - selectedLabel.getOffsetLeft();
        labelBeforePos = labelAfterPos - selectedLabel.getOffsetWidth();
      }
    } else {
      labelBeforePos = selectedLabel.getOffsetTop();
      labelAfterPos = labelBeforePos + selectedLabel.getOffsetHeight();
    }
    const /** @type {?} */ beforeVisiblePos = this.scrollDistance;
    const /** @type {?} */ afterVisiblePos = this.scrollDistance + this.viewWidthHeightPix;

    if (labelBeforePos < beforeVisiblePos) {
      // Scroll header to move label to the before direction
      this.scrollDistance -= beforeVisiblePos - labelBeforePos + EXAGGERATED_OVERSCROLL;
    } else if (labelAfterPos > afterVisiblePos) {
      // Scroll header to move label to the after direction
      this.scrollDistance += labelAfterPos - afterVisiblePos + EXAGGERATED_OVERSCROLL;
    }
  }
/**
 * @return {?}
 */
_checkScrollingControls(): void {
    // Check if the pagination arrows should be activated.
    this._disableScrollBefore = this.scrollDistance === 0;
    this._disableScrollAfter = this.scrollDistance === this._getMaxScrollDistance();
  }
/**
 * Determines what is the maximum length in pixels that can be set for the scroll distance. This
 * is equal to the difference in width between the tab list container and tab header container.
 * 
 * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
 * should be called sparingly.
 * @return {?}
 */
_getMaxScrollDistance(): number {
    return (this.tabListScrollWidthHeightPix - this.viewWidthHeightPix) || 0;
  }
/**
 * Sets the distance in pixels that the tab header should be transformed in the X-axis.
 * @param {?} v
 * @return {?}
 */
set scrollDistance(v: number) {
    this._scrollDistance = Math.max(0, Math.min(this._getMaxScrollDistance(), v));

    // Mark that the scroll distance has changed so that after the view is checked, the CSS
    // transformation can move the header.
    this._scrollDistanceChanged = true;

    this._checkScrollingControls();
  }
/**
 * @return {?}
 */
get scrollDistance(): number {
    return this._scrollDistance;
  }
/**
 * @return {?}
 */
get viewWidthHeightPix(): number {
    let /** @type {?} */ PAGINATION_PIX = 0;
    if (this._showPaginationControls) {
      PAGINATION_PIX = 64;
    }
    if (this.nzPositionMode === 'horizontal') {
      return this._tabListContainer.nativeElement.offsetWidth - PAGINATION_PIX;
    } else {
      return this._tabListContainer.nativeElement.offsetHeight - PAGINATION_PIX;
    }
  }
/**
 * @return {?}
 */
get tabListScrollWidthHeightPix(): number {
    if (this.nzPositionMode === 'horizontal') {
      return this._tabList.nativeElement.scrollWidth;
    } else {
      return this._tabList.nativeElement.scrollHeight;
    }
  }
/**
 * @return {?}
 */
get elementRefOffSetWidthHeight(): number {
    if (this.nzPositionMode === 'horizontal') {
      return this._elementRef.nativeElement.offsetWidth;
    } else {
      return this._elementRef.nativeElement.offsetHeight;
    }
  }
/**
 * @return {?}
 */
_getLayoutDirection(): Direction {
    return this._dir && this._dir.value === 'rtl' ? 'rtl' : 'ltr';
  }
/**
 * @return {?}
 */
_alignInkBarToSelectedTab(): void {
    if (this.nzType === 'line') {
      const /** @type {?} */ selectedLabelWrapper = this._labelWrappers && this._labelWrappers.length
        ? this._labelWrappers.toArray()[ this.selectedIndex ].elementRef.nativeElement
        : null;
      if (this._inkBar) {
        this._inkBar.alignToElement(selectedLabelWrapper);
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector     : 'yzt-tabs-nav',
  encapsulation: ViewEncapsulation.None,
  template     : `
    <div style="float:right;" *ngIf="_tabBarExtraContent">
      <div class="ant-tabs-extra-content">
        <ng-template [ngTemplateOutlet]="_tabBarExtraContent">
        </ng-template>
      </div>
    </div>
    <div class="yzt-tabs-nav-container" [class.ant-tabs-nav-container-scrolling]="_showPaginationControls" #tabListContainer>
      <span class="ant-tabs-tab-prev ant-tabs-tab-arrow-show" (click)="_scrollHeader('before')" [class.ant-tabs-tab-btn-disabled]="_disableScrollBefore" *ngIf="_showPaginationControls">
        <span class="ant-tabs-tab-prev-icon"></span>
      </span>
      <span class="ant-tabs-tab-next ant-tabs-tab-arrow-show" (click)="_scrollHeader('after')" [class.ant-tabs-tab-btn-disabled]="_disableScrollAfter" *ngIf="_showPaginationControls">
        <span class="ant-tabs-tab-next-icon"></span>
      </span>
      <div class="ant-tabs-nav-wrap">
        <div class="ant-tabs-nav-scroll">
          <div class="ant-tabs-nav" [class.ant-tabs-nav-animated]="nzAnimated" #tabList (cdkObserveContent)="_onContentChanges()">
            <div yzt-tabs-ink-bar [hidden]="nzHideBar" [nzAnimated]="nzAnimated" [nzPositionMode]="nzPositionMode" style="display: block;"></div>
            <ng-content></ng-content>
          </div>
        </div>
      </div>
    </div>
  `,
  host: {
    '[class.ant-tabs-bar]': 'true'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: Renderer2, },
{type: Directionality, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'nzSize': [{ type: Input },],
'nzAnimated': [{ type: Input },],
'nzHideBar': [{ type: Input },],
'nzType': [{ type: Input },],
'_tabBarExtraContent': [{ type: ContentChild, args: ['tabBarExtraContent', ] },],
'_labelWrappers': [{ type: ContentChildren, args: [YztTabLabelDirective, ] },],
'_inkBar': [{ type: ViewChild, args: [YztTabsInkBarDirective, ] },],
'_tabListContainer': [{ type: ViewChild, args: ['tabListContainer', ] },],
'_tabList': [{ type: ViewChild, args: ['tabList', ] },],
'nzShowPagination': [{ type: Input },],
'nzPositionMode': [{ type: Input },],
'selectedIndex': [{ type: Input },],
};
}

function YztTabsNavComponent_tsickle_Closure_declarations() {
/** @type {?} */
YztTabsNavComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
YztTabsNavComponent.ctorParameters;
/** @type {?} */
YztTabsNavComponent.propDecorators;
/** @type {?} */
YztTabsNavComponent.prototype._animated;
/** @type {?} */
YztTabsNavComponent.prototype._hideBar;
/** @type {?} */
YztTabsNavComponent.prototype._showPagination;
/** @type {?} */
YztTabsNavComponent.prototype._showPaginationControls;
/** @type {?} */
YztTabsNavComponent.prototype._disableScrollAfter;
/** @type {?} */
YztTabsNavComponent.prototype._disableScrollBefore;
/** @type {?} */
YztTabsNavComponent.prototype._scrollDistance;
/** @type {?} */
YztTabsNavComponent.prototype._selectedIndexChanged;
/** @type {?} */
YztTabsNavComponent.prototype._realignInkBar;
/** @type {?} */
YztTabsNavComponent.prototype._tabLabelCount;
/** @type {?} */
YztTabsNavComponent.prototype._scrollDistanceChanged;
/** @type {?} */
YztTabsNavComponent.prototype._selectedIndex;
/** @type {?} */
YztTabsNavComponent.prototype._tabPositionMode;
/** @type {?} */
YztTabsNavComponent.prototype.nzSize;
/** @type {?} */
YztTabsNavComponent.prototype._type;
/** @type {?} */
YztTabsNavComponent.prototype._tabBarExtraContent;
/** @type {?} */
YztTabsNavComponent.prototype._labelWrappers;
/** @type {?} */
YztTabsNavComponent.prototype._inkBar;
/** @type {?} */
YztTabsNavComponent.prototype._tabListContainer;
/** @type {?} */
YztTabsNavComponent.prototype._tabList;
/** @type {?} */
YztTabsNavComponent.prototype._elementRef;
/** @type {?} */
YztTabsNavComponent.prototype._ngZone;
/** @type {?} */
YztTabsNavComponent.prototype._renderer;
/** @type {?} */
YztTabsNavComponent.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
